(() => {
  'use strict';
  if (document.getElementById('cdx-free-position-ui')) return;
  const configNode = document.getElementById('cdx-free-position-config');
  if (!configNode) return;
  let config;
  try { config = configNode.hasAttribute('data-positions') ? {designMode: configNode.dataset.designMode === 'true', positions: configNode.dataset.positions} : JSON.parse(configNode.textContent); } catch { return; }
  const SELECTOR = '.shopify-section[id^="shopify-section-"],[data-shopify-editor-block]';
  const CHANNEL = 'sealy-free-position-v1';
  const editorOrigin = config.editorOrigin || 'https://online-store-web.shopifyapps.com';
  const empty = () => ({v: 1, items: {}});
  const parse = raw => {
    try { const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return value?.v === 1 && value.items && typeof value.items === 'object' && !Array.isArray(value.items) ? value : empty();
    } catch { return empty(); }
  };
  let model = parse(config.positions);
  const originals = new Map();
  const clipping = new Map();
  const dimensions = new Map();
  const MIN_HEIGHT = 32;
  const resizable = el => Boolean(el?.matches('.shopify-section,.group-block'));
  const hasHeight = value => Number.isFinite(value?.height) && value.height >= MIN_HEIGHT && value.height <= 1000000;
  function resizeBox(el) {
    if (el.getBoundingClientRect().height > 0) return el;
    return [...el.children].find(node => node.matches('header-component,.section') && node.getBoundingClientRect().height > 0) || el;
  }
  function restoreDimensions(el) {
    const saved = dimensions.get(el);
    if (saved) for (const [node, values] of saved) restoreStyle(node, values);
    dimensions.delete(el);
  }
  function applyDimensions(el, value) {
    restoreDimensions(el);
    if (!hasHeight(value)) return;
    const saved = new Map(); dimensions.set(el, saved);
    const set = (node, values) => {
      if (!saved.has(node)) saved.set(node, {});
      const original = saved.get(node);
      for (const [property, next] of Object.entries(values)) {
        if (!(property in original)) original[property] = [node.style.getPropertyValue(property), node.style.getPropertyPriority(property)];
        node.style.setProperty(property, next, 'important');
      }
    };
    // Keep subsequent sections at the anchored bottom when dragging the top.
    const extra = Number.isFinite(value.resizeTop) ? value.resizeTop : 0;
    const box = resizeBox(el), bottom = getComputedStyle(el).marginBottom;
    set(box, {'height':value.height + 'px','min-height':'0px','max-height':'none','box-sizing':'border-box'});
    if (box === el) set(el, {'margin-bottom':'calc(' + bottom + ' + ' + extra + 'px)'});
    // Theme wrappers own their background and minimum height. Resize their
    // container without scaling text, icons, images or the 3D model.
    for (const child of el.children) if (child.matches('.section,.group-block-content,header-component')) {
      set(child, {'height':child === box ? value.height + 'px' : '100%','min-height':'0px','max-height':'none','box-sizing':'border-box','--section-min-height':'0px','--top-row-height':value.height + 'px'});
    }
  }
  // A real desktop layout viewport is kept before applying one uniform zoom.
  // Width media queries and viewport units are evaluated at that same width,
  // so mobile CSS cannot independently rearrange the freely positioned blocks.
  function createCompositionController(changed) {
    const sheets = new Map(), inline = new Map(), bodySaved = {}, scrollerSaved = {};
    let width = 0, requested = 0, scale = 1, revision = 0, active = false, loading = false, failure = '';
    const output = document.createElement('style'); output.dataset.cdxComposition = 'true';
    const own = node => node === output || node?.dataset?.cdxPositionUi === 'true';
    const px = (value, unit) => Number(value) * (/^(em|rem)$/i.test(unit) ? 16 : 1);
    function condition(text, reference) {
      const truth = yes => yes ? '(min-width:0px)' : '(max-width:-1px)';
      text = text.replace(/\(\s*(min|max)-width\s*:\s*([\d.]+)(px|em|rem)\s*\)/gi,
        (_, bound, value, unit) => truth(bound.toLowerCase() === 'min' ? reference >= px(value,unit) : reference <= px(value,unit)));
      const compare = (left, op, right) => op === '>=' ? left >= right : op === '<=' ? left <= right : op === '>' ? left > right : op === '<' ? left < right : left === right;
      text = text.replace(/\(\s*([\d.]+)(px|em|rem)\s*(<=|<)\s*width\s*(<=|<)\s*([\d.]+)(px|em|rem)\s*\)/gi,
        (_, a, au, ao, bo, b, bu) => truth(compare(px(a,au),ao,reference) && compare(reference,bo,px(b,bu))));
      text = text.replace(/\(\s*width\s*(>=|<=|>|<|:)\s*([\d.]+)(px|em|rem)\s*\)/gi,
        (_, op, value, unit) => truth(compare(reference,op,px(value,unit))));
      text = text.replace(/\(\s*([\d.]+)(px|em|rem)\s*(>=|<=|>|<)\s*width\s*\)/gi,
        (_, value, unit, op) => truth(compare(px(value,unit),op,reference)));
      return text;
    }
    function viewportUnits(text, reference) {
      // Skip strings, comments and URL tokens rather than rewriting asset names.
      return text.replace(/\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|url\([^)]*\)|(-?(?:\d*\.)?\d+)(?:s|l|d)?vw\b/gi,
        (token, number) => number === undefined ? token : String(Math.round(Number(number) * reference * 1000 / 100) / 1000) + 'px');
    }
    function cssText(text, reference, base) {
      text = text.replace(/@media\s+([^{}]+)\{/gi, (_, query) => '@media ' + condition(query,reference) + '{');
      text = viewportUnits(text,reference);
      if (base) text = text.replace(/url\(\s*(['"]?)([^'"()]+)\1\s*\)/gi, (all, quote, url) => {
        if (/^(data:|#)/i.test(url.trim())) return all;
        try { return 'url(' + JSON.stringify(new URL(url.trim(),base).href) + ')'; } catch { return all; }
      });
      return text;
    }
    async function source(sheet) {
      try {
        let value = '';
        for (const rule of sheet.cssRules) {
          if (rule.type === CSSRule.IMPORT_RULE) {
            const imported = await source(rule.styleSheet);
            value += rule.media?.mediaText ? '@media ' + condition(rule.media.mediaText,requested) + '{' + imported + '}' : imported;
          } else value += cssText(rule.cssText,requested,sheet.href || document.baseURI) + '\n';
        }
        return value;
      } catch (error) {
        if (!sheet.href) throw error;
        const response = await fetch(sheet.href, {credentials:'omit'});
        if (!response.ok) throw new Error('Feuille de style indisponible.');
        const text = await response.text();
        // Imported sheets must be resolved too; leave the original layout intact
        // if a cross-origin stylesheet uses an import we cannot inspect safely.
        if (/@import\b/i.test(text)) throw new Error('Import CSS non pris en charge.');
        return cssText(text,requested,sheet.href);
      }
    }
    function style(el, property, value, saved) {
      if (!(property in saved)) saved[property] = [el.style.getPropertyValue(property), el.style.getPropertyPriority(property)];
      el.style.setProperty(property,value,'important');
    }
    function applyScale() {
      if (!active) return;
      scale = document.documentElement.clientWidth / width;
      style(document.body,'width',width+'px',bodySaved);
      style(document.body,'min-width',width+'px',bodySaved);
      style(document.body,'max-width',width+'px',bodySaved);
      style(document.body,'zoom',String(scale),bodySaved);
      // Horizon scrolls inside a page wrapper whose 100vh does not account
      // for CSS zoom. Give the scroller one full visible viewport.
      const scroller = document.querySelector('.page-wrapper');
      if (scroller) {
        style(document.body,'height',innerHeight/scale+'px',bodySaved);
        style(document.body,'min-height',innerHeight/scale+'px',bodySaved);
        style(scroller,'height',innerHeight/scale+'px',scrollerSaved);
        style(scroller,'min-height',innerHeight/scale+'px',scrollerSaved);
      } else {
        style(document.body,'height','auto',bodySaved);
        style(document.body,'min-height',innerHeight/scale+'px',bodySaved);
        style(document.body,'overflow','visible',bodySaved);
      }
      style(document.body,'margin-left','auto',bodySaved);
      style(document.body,'margin-right','auto',bodySaved);
      document.documentElement.style.setProperty('--cdx-composition-inverse',String(1/scale));
      for (const el of document.querySelectorAll('[style]')) {
        if (own(el) || el.closest('#cdx-free-position-ui,#cdx-free-resize-ui')) continue;
        for (const property of [...el.style]) {
          const value = el.style.getPropertyValue(property);
          if (!/(?:\d)(?:s|l|d)?vw\b/i.test(value)) continue;
          if (!inline.has(el)) inline.set(el,{});
          style(el,property,viewportUnits(value,width),inline.get(el));
        }
      }
    }
    function disable() {
      revision++; requested = width = 0; active = loading = false; scale = 1; failure = '';
      for (const [sheet, wasDisabled] of sheets) sheet.disabled = wasDisabled;
      sheets.clear(); output.remove();
      restoreStyle(document.body,bodySaved);
      for (const key of Object.keys(bodySaved)) delete bodySaved[key];
      const scroller = document.querySelector('.page-wrapper');
      if (scroller) restoreStyle(scroller,scrollerSaved);
      for (const key of Object.keys(scrollerSaved)) delete scrollerSaved[key];
      for (const [el, values] of inline) restoreStyle(el,values);
      inline.clear(); document.documentElement.style.removeProperty('--cdx-composition-inverse');
    }
    async function build(reference) {
      const version = ++revision; loading = true; failure = '';
      try {
        const sources = [...document.styleSheets].filter(sheet => !own(sheet.ownerNode) && (!sheet.disabled || sheets.has(sheet)));
        const text = (await Promise.all(sources.map(async sheet => {
          const value = await source(sheet), media = sheet.media?.mediaText;
          return media ? '@media ' + condition(media,reference) + '{' + value + '}' : value;
        }))).join('\n');
        if (version !== revision || requested !== reference) return;
        output.textContent = text;
        if (!output.isConnected) document.head.append(output);
        for (const sheet of sources) { if (!sheets.has(sheet)) sheets.set(sheet,sheet.disabled); sheet.disabled = true; }
        width = reference; active = true; applyScale();
      } catch (error) {
        if (version !== revision) return;
        disable(); requested = reference; failure = error.message || 'Styles indisponibles.';
      } finally { if (version === revision || failure) { loading = false; changed(); } }
    }
    new MutationObserver(records => {
      if (!active || loading) return;
      const relevant = records.some(record => !own(record.target) && !own(record.target.parentElement) &&
        (record.target.closest?.('style') || [...record.addedNodes,...record.removedNodes].some(node => node.nodeType === 1 && !own(node) && (node.matches?.('style,link[rel="stylesheet"]') || node.querySelector?.('style,link[rel="stylesheet"]')))));
      if (relevant) build(requested);
    }).observe(document.documentElement,{subtree:true,childList:true,characterData:true});
    return {
      update(value) {
        const next = value?.v === 1 && Number.isFinite(value.width) && value.width >= 750 && value.width <= 3840 ? value.width : 0;
        if (!next) { if (requested || active) disable(); return; }
        if (next !== requested) { requested = next; build(next); }
        else applyScale();
      },
      get scale() { return scale; }, get active() { return active; }, get loading() { return loading; }, get error() { return failure; },
      condition, viewportUnits
    };
  }

  const composition = createCompositionController(() => { refresh(); });
  const compositionSetting = () => {
    if (matchMedia('(max-width: 749px)').matches) return null;
    const explicit = model.compositions?.[location.pathname];
    if (explicit) return explicit;
    // Only pages with freely placed template content need the fixed canvas.
    // Header positions are shared across pages and do not trigger it alone.
    const placed = Object.entries(model.items).some(([id, item]) =>
      item?.section?.startsWith('shopify-section-template--') && item.wide && resolve(item, id));
    return placed ? {v:1,width:1440} : null;
  };
  const size = () => composition.active ? 'wide' : matchMedia('(max-width: 749px)').matches ? 'narrow' : 'wide';
  const esc = value => CSS.escape(value);
  function meta(el) {
    if (!el) return null;
    if (el.hasAttribute('data-shopify-editor-block')) {
      try { return JSON.parse(el.getAttribute('data-shopify-editor-block')); } catch { return null; }
    }
    return el.classList.contains('shopify-section') ? {id: el.id.slice('shopify-section-'.length), section: true} : null;
  }
  function matchesOne(root, selector, target) {
    try { const matches = root.querySelectorAll(selector); return matches.length === 1 && matches[0] === target; } catch { return false; }
  }
  function selectorFor(el, section) {
    if (el === section) return [':scope'];
    const selectors = [];
    const add = selector => { if (matchesOne(section, selector, el) && !selectors.includes(selector)) selectors.push(selector); };
    if (el.id) add('#' + esc(el.id));
    const data = meta(el);
    const classes = [...el.classList].filter(c => !/^cdx-|^shopify-design-mode|^is-|^active$|^selected$/.test(c));
    classes.sort((a,b) => Number(b.includes(data?.domId || '\0')) - Number(a.includes(data?.domId || '\0')));
    for (const name of classes) { add(el.localName + '.' + esc(name)); if (selectors.length >= 3) break; }
    for (const attr of ['data-block-id', 'data-id', 'ref']) {
      const value = el.getAttribute(attr); if (value) add(el.localName + '[' + attr + '="' + value.replace(/\\/g,'\\\\').replace(/"/g,'\\"') + '"]');
    }
    if (selectors.length < 2) {
      let current = el; const parts = [];
      while (current && current !== section) {
        const parent = current.parentElement; if (!parent) break;
        const peers = [...parent.children].filter(x => x.localName === current.localName);
        parts.unshift(current.localName + ':nth-of-type(' + (peers.indexOf(current) + 1) + ')');
        if (current.id && current !== el) { add('#' + esc(current.id) + ' > ' + parts.slice(1).join(' > ')); break; }
        current = parent;
      }
      add(':scope > ' + parts.join(' > '));
    }
    return selectors.slice(0, 6);
  }
  function describe(el) {
    const data = meta(el); const section = el.closest('.shopify-section[id]');
    if (!data?.id || !section) return null;
    return {id: data.section ? data.id : data.id + '::' + (data.domId || ''), editorId: data.id, domId: data.domId || '', section: section.id, tag: el.localName, selectors: selectorFor(el, section)};
  }
  function resolve(item, id) {
    const section = document.getElementById(item.section);
    if (!section) return null;
    if (item.selectors?.[0] === ':scope') return section;
    if (config.designMode) {
      const block = [...section.querySelectorAll('[data-shopify-editor-block]')].find(el => { const data = meta(el); return data?.id === (item.editorId || id) && (!item.domId || data.domId === item.domId); });
      // In the editor an absent ID is a deleted/hidden block, never its new
      // neighbour at the same nth-of-type selector. CSS fallback is storefront-only.
      return block || null;
    }
    for (const selector of item.selectors || []) {
      try { const hits = section.querySelectorAll(selector); if (hits.length === 1 && hits[0].localName === item.tag) return hits[0]; } catch { /* Ignore obsolete selectors. */ }
    }
    return null;
  }
  const FOREGROUND = '.text-block,.icon-block,.icon-block__media,.social-icons__wrapper,.footer-utilities__group-copyright,.button-custom,.button-unstyled,product-price';
  const MEDIA = '.image-block,.card-gallery,media-gallery';
  function priority(el) {
    if (el.matches(FOREGROUND)) return 3;
    if (el.classList.contains('shopify-section') || el.matches('.group-block,.card,product-card')) return 0;
    const type = (meta(el)?.id || '').split('__').pop();
    if (/^(text|heading|product_title|product_list_text|price|icon|social_links|footer_copyright|button|header-logo)(_|$)/.test(type)) return 3;
    if (el.matches('.image-block,.card-gallery,media-gallery,img,picture,video,model-viewer,canvas,iframe,svg')) return 1;
    return 2;
  }
  const properties = ['translate', 'position', 'z-index', 'left', 'top', 'right', 'bottom'];
  let foregroundActive = false;
  function capture(el) {
    if (!originals.has(el)) originals.set(el, Object.fromEntries(properties.map(p => [p, [el.style.getPropertyValue(p), el.style.getPropertyPriority(p)]])));
  }
  function restoreStyle(el, values) { for (const [property, [value, priority]] of Object.entries(values)) { if (value) el.style.setProperty(property, value, priority); else el.style.removeProperty(property); } }
  function foreground(el) {
    capture(el);
    const css = getComputedStyle(el);
    if (css.position === 'static') el.style.setProperty('position', 'relative');
    el.style.setProperty('z-index', String(Math.max(2, Number(css.zIndex) || 0)));
  }
  function ensureForeground() {
    if (foregroundActive) return;
    // Horizon's grid content wrappers otherwise isolate each section at z-index: 1.
    for (const el of document.querySelectorAll('.custom-section-content')) {
      if (el.closest('.shopify-section')) { capture(el); el.style.setProperty('z-index', 'auto', 'important'); }
    }
    for (const el of document.querySelectorAll(MEDIA)) if (el.closest('.shopify-section')) mediaLayer(el);
    for (const el of document.querySelectorAll(FOREGROUND)) if (el.closest('.shopify-section')) foreground(el);
    foregroundActive = true;
  }
  function mediaLayer(el) {
    capture(el);
    if (getComputedStyle(el).position === 'static') el.style.setProperty('position', 'relative');
    el.style.setProperty('z-index', '0');
  }
  function applyOne(el, offset) {
    capture(el); restoreStyle(el, originals.get(el));
    applyDimensions(el, offset);
    if (!offset || !Number.isFinite(offset.x) || !Number.isFinite(offset.y)) return;
    const y = offset.y + (hasHeight(offset) && Number.isFinite(offset.resizeTop) ? offset.resizeTop : 0);
    if (!offset.x && !y) return;
    const computed = getComputedStyle(el);
    if (computed.position === 'static' || computed.position === 'relative') {
      // Relative offsets preserve the section's original stacking context.
      const baseX = computed.position === 'static' || computed.left === 'auto' ? '0px' : computed.left;
      const baseY = computed.position === 'static' || computed.top === 'auto' ? '0px' : computed.top;
      el.style.setProperty('position', 'relative');
      el.style.setProperty('left', 'calc(' + baseX + ' + ' + offset.x + 'px)', 'important');
      el.style.setProperty('top', 'calc(' + baseY + ' + ' + y + 'px)', 'important');
      el.style.setProperty('right', 'auto', 'important');
      el.style.setProperty('bottom', 'auto', 'important');
    } else {
      const base = computed.translate === 'none' ? ['0px', '0px'] : computed.translate.split(/\s+/);
      el.style.setProperty('translate', 'calc(' + (base[0] || '0px') + ' + ' + offset.x + 'px) calc(' + (base[1] || '0px') + ' + ' + y + 'px)' + (base[2] ? ' ' + base[2] : ''), 'important');
    }
    if (priority(el) === 3) foreground(el);
    else if (el.matches(MEDIA)) mediaLayer(el);
    for (let parent = el.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
      const css = getComputedStyle(parent);
      if (parent.matches('slideshow-slides, slideshow-container')) continue;
      if ([css.overflowX, css.overflowY].some(v => v === 'hidden' || v === 'clip')) {
        if (!clipping.has(parent)) clipping.set(parent, {overflow: [parent.style.getPropertyValue('overflow'), parent.style.getPropertyPriority('overflow')]});
        parent.style.setProperty('overflow', 'visible', 'important');
      }
    }
  }
  function applyAll() {
    composition.update(compositionSetting());
    for (const el of [...dimensions.keys()]) restoreDimensions(el);
    for (const [el, values] of originals) { restoreStyle(el, values); if (!el.isConnected) originals.delete(el); }
    for (const [el, values] of clipping) restoreStyle(el, values);
    clipping.clear();
    foregroundActive = false;
    let positioned = false;
    for (const [id, item] of Object.entries(model.items)) {
      if (!item || typeof item.section !== 'string') continue;
      if (size() === 'narrow' && /^shopify-section-(template--30718308581703__|sections--30718308745543__header_section)/.test(item.section)) continue;
      const el = resolve(item, id), offset = item[size()];
      if (el && offset && (offset.x || offset.y || hasHeight(offset))) { applyOne(el, offset); positioned = true; }
    }
    if (positioned) ensureForeground();
  }
  let reconcileSelection = () => {};
  let scheduled = false;
  const refresh = () => { if (scheduled) return; scheduled = true; requestAnimationFrame(() => { scheduled = false; applyAll(); reconcileSelection(); }); };
  new MutationObserver(records => {
    if (records.some(record => !record.target?.closest?.('#cdx-free-position-ui,#cdx-free-resize-ui') && [...record.addedNodes, ...record.removedNodes].some(n => n instanceof Element))) refresh();
  }).observe(document.body, {childList: true, subtree: true});
  window.addEventListener('resize', refresh);
  document.addEventListener('shopify:section:load', refresh);
  applyAll();
  if (!config.designMode || window.parent === window) return;

  const style = document.createElement('style'); style.dataset.cdxPositionUi = 'true';
  style.textContent = '#cdx-free-position-ui{position:fixed;top:10px;right:12px;z-index:2147483647;font:12px/1.35 system-ui;color:#fafafa;background:#242424;border-radius:8px;padding:8px;box-shadow:0 2px 9px #0004;max-width:320px}#cdx-free-position-ui button,#cdx-free-position-ui select{font:inherit;border:1px solid #666;border-radius:5px;padding:5px 7px;background:#393939;color:#fff;margin:2px;max-width:250px}#cdx-free-position-ui button[aria-pressed="true"]{background:#14663f}#cdx-free-position-ui button:disabled{opacity:.5}#cdx-free-position-ui [data-status]{display:block;padding:4px 3px;color:#eee}#cdx-free-position-ui [data-controls][hidden]{display:none}html.cdx-free-active [data-shopify-editor-block],html.cdx-free-active .shopify-section{cursor:grab!important}html.cdx-free-dragging,html.cdx-free-dragging *{cursor:grabbing!important;user-select:none!important;touch-action:none!important}.cdx-free-selected{outline:2px solid #238aff!important;outline-offset:-2px}';
  style.textContent += "#cdx-free-position-ui [data-fold]{float:right;width:30px;height:28px;padding:0;font-size:20px;line-height:1}#cdx-free-position-ui.cdx-free-collapsed{padding:4px}#cdx-free-position-ui.cdx-free-collapsed>[data-toggle],#cdx-free-position-ui.cdx-free-collapsed>[data-controls],#cdx-free-position-ui.cdx-free-collapsed>[data-status]{display:none}#cdx-free-position-ui.cdx-free-collapsed [data-fold]{float:none;margin:0}html.cdx-free-active #cdx-free-position-ui.cdx-free-collapsed [data-fold]{background:#14663f}";
  document.head.append(style);
  const panel = document.createElement('div'); panel.id = 'cdx-free-position-ui';
  panel.dataset.version = '0.3.0';
  panel.innerHTML = '<button type="button" data-toggle aria-pressed="false">Déplacement libre</button><button type="button" data-fold aria-label="Réduire le panneau" title="Réduire le panneau" aria-expanded="true">−</button><div data-controls hidden><select aria-label="Élément à déplacer"></select><button type="button" data-reset>Réinitialiser</button></div><span data-status role="status">Active le mode pour placer les éléments.</span>';
  document.body.append(panel);
  const toggle = panel.querySelector('[data-toggle]'), controls = panel.querySelector('[data-controls]'), select = panel.querySelector('select'), reset = panel.querySelector('[data-reset]'), status = panel.querySelector('[data-status]');
  style.textContent += '#cdx-free-position-ui,#cdx-free-resize-ui{zoom:var(--cdx-composition-inverse,1)!important}';
  const fold = panel.querySelector('[data-fold]');
  let collapsed = false;
  try { collapsed = sessionStorage.getItem('cdx-free-position-panel-collapsed') === '1'; } catch {}
  function foldPanel(value) {
    collapsed = value;
    panel.classList.toggle('cdx-free-collapsed', collapsed);
    fold.textContent = collapsed ? '↗' : '−';
    const label = collapsed ? 'Afficher le panneau de déplacement' : 'Réduire le panneau';
    fold.setAttribute('aria-label', label); fold.title = label;
    fold.setAttribute('aria-expanded', String(!collapsed));
    try { sessionStorage.setItem('cdx-free-position-panel-collapsed', collapsed ? '1' : '0'); } catch {}
  }
  fold.addEventListener('click', () => foldPanel(!collapsed));
  foldPanel(collapsed);
  const resizeUI = document.createElement('div'); resizeUI.id = 'cdx-free-resize-ui';
  resizeUI.innerHTML = '<button type="button" data-edge="top" aria-label="Redimensionner la section par le haut" title="Glisser pour régler la hauteur depuis le haut"></button><button type="button" data-edge="bottom" aria-label="Redimensionner la section par le bas" title="Glisser pour régler la hauteur depuis le bas"></button>';
  resizeUI.hidden = true; document.body.append(resizeUI);
  style.textContent += '#cdx-free-resize-ui{position:fixed;inset:0;z-index:2147483646;pointer-events:none}#cdx-free-resize-ui[hidden]{display:none}#cdx-free-resize-ui button{position:absolute;box-sizing:border-box;height:14px;padding:0;min-width:28px;border:0;border-top:2px solid #238aff;background:transparent;pointer-events:auto;cursor:ns-resize!important;touch-action:none}#cdx-free-resize-ui button:after{content:"";position:absolute;left:calc(50% - 20px);top:-5px;width:40px;height:8px;border:2px solid #fff;border-radius:5px;background:#238aff;box-shadow:0 1px 4px #0005}html.cdx-free-resizing,html.cdx-free-resizing *{cursor:ns-resize!important;user-select:none!important;touch-action:none!important}';
  function updateResizeHandles() {
    const available = enabled && !connecting && !validating && selected?.isConnected && resizable(selected);
    const rect = available && resizeBox(selected).getBoundingClientRect();
    resizeUI.hidden = !rect || rect.width <= 0 || rect.height <= 0;
    if (resizeUI.hidden) return;
    for (const button of resizeUI.children) {
      const edgeY = button.dataset.edge === 'top' ? rect.top : rect.bottom;
      button.hidden = edgeY < 0 || edgeY > innerHeight;
      button.style.left = Math.max(0, rect.left) + 'px';
      button.style.width = Math.max(0, Math.min(innerWidth, rect.right) - Math.max(0, rect.left)) + 'px';
      button.style.top = Math.min(innerHeight - 14, Math.max(0, edgeY - 6)) + 'px';
    }
  }
  window.addEventListener('scroll', updateResizeHandles, true);
  window.addEventListener('resize', updateResizeHandles);
  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(updateResizeHandles) : null;
  let enabled = false, connecting = false, validating = false, selected = null, selectedDescription = null, ancestors = [], lockedSelection = false;
  let drag = null, pending = false, timer = null, countdown = null, generation = 0, acknowledged = JSON.stringify(model);
  // Shopify can rebuild the preview while a native field/picker is being edited.
  // Keep only the unvalidated delta, scoped to this theme asset and this page.
  const assetPath = [...document.scripts].find(script => /\/free-position\.js(?:\?|$)/.test(script.src))?.src;
  const pendingKey = 'cdx-free-position-pending:' + (assetPath ? new URL(assetPath).pathname : '') + ':' + location.pathname;
  function rememberPending() {
    try {
      if (pending) sessionStorage.setItem(pendingKey, JSON.stringify({at:Date.now(),before:acknowledged,local:JSON.stringify(model)}));
      else sessionStorage.removeItem(pendingKey);
    } catch {}
  }
  try {
    const stored = JSON.parse(sessionStorage.getItem(pendingKey) || 'null');
    if (stored && Date.now() - stored.at < 1800000) {
      mergePending(model, parse(stored.before), parse(stored.local));
      pending = JSON.stringify(model) !== acknowledged;
      if (pending) generation++;
      applyAll();
    } else sessionStorage.removeItem(pendingKey);
  } catch {}
  const requests = new Map();
  const say = text => { status.textContent = text; };
  let selectionSupported = false;
  function selectionFor(el) {
    if (!el) return undefined;
    const path = [];
    for (let node = el; node; node = node.parentElement?.closest(SELECTOR)) {
      const id = meta(node)?.id; if (id && !path.includes(id)) path.unshift(id);
    }
    return path.length ? {path} : undefined;
  }
  async function openSettings(el) {
    const selection = selectionFor(el); if (!selection) return;
    if (!selectionSupported) { say('Recharge l’extension Position libre 0.1.2 pour ouvrir les réglages au clic.'); foldPanel(false); return; }
    try { await request('select', undefined, selection); }
    catch (error) { say(error.message); foldPanel(false); }
  }
  function request(type, positions, selection) {
    return new Promise((resolve, reject) => {
      const requestId = String(Date.now()) + '-' + Math.random().toString(36).slice(2);
      const timeout = setTimeout(() => { requests.delete(requestId); reject(new Error('Connexion Chrome nécessaire pour valider dans Shopify.')); }, 9000);
      requests.set(requestId, {resolve, reject, timeout});
      parent.postMessage({channel: CHANNEL, type, requestId, positions, selection}, editorOrigin);
    });
  }
  window.addEventListener('message', event => {
    if (event.source !== parent || event.origin !== editorOrigin || event.data?.channel !== CHANNEL) return;
    if (event.data.type === 'native-selection') { syncNativeSelection(event.data.selection); return; }
    if (event.data.type !== 'response') return;
    const item = requests.get(event.data.requestId); if (!item) return;
    requests.delete(event.data.requestId); clearTimeout(item.timeout);
    if (event.data.ok) item.resolve(event.data); else item.reject(new Error(event.data.error || 'Validation refusée.'));
  });
  function rememberView() {
    try { sessionStorage.setItem('cdx-free-position-view', JSON.stringify({path:location.pathname,x:scrollX,y:scrollY,selected:selected && describe(selected),choices:ancestors.map(describe).filter(Boolean),locked:lockedSelection})); } catch {}
  }
  function restoreView() {
    try {
      const raw = sessionStorage.getItem('cdx-free-position-view');
      sessionStorage.removeItem('cdx-free-position-view');
      const view = raw && JSON.parse(raw); if (!view || view.path !== location.pathname) return;
      const first = view.choices?.[0], chosen = view.selected;
      const target = chosen && resolve(chosen, chosen.id);
      if (target) { selectElement(first && resolve(first, first.id) || target); selectElement(target, true); nativeSelection = selectionFor(target); lockedSelection = Boolean(view.locked); }
      const restoreScroll = () => requestAnimationFrame(() => requestAnimationFrame(() => scrollTo(view.x || 0, view.y || 0)));
      restoreScroll(); if (document.readyState !== 'complete') window.addEventListener('load', restoreScroll, {once:true});
    } catch {}
  }
  function stopTimer() { clearTimeout(timer); clearInterval(countdown); timer = countdown = null; }
  function schedule() {
    stopTimer(); if (!pending || drag || validating) return;
    const start = Date.now();
    say('Position en attente · validation dans 3 s');
    countdown = setInterval(() => say('Position en attente · validation dans ' + Math.max(1, Math.ceil((3000 - (Date.now() - start))/1000)) + ' s'), 200);
    timer = setTimeout(validate, 3000);
  }
  async function validate() {
    stopTimer(); if (!pending || drag || validating) return;
    const value = JSON.stringify(model), version = generation;
    if (value === acknowledged) { pending = false; rememberPending(); say('Position inchangée.'); return; }
    validating = true; toggle.disabled = reset.disabled = select.disabled = true;
    rememberView();
    say('Validation dans Shopify…');
    try {
      const response = await request('validate', value, selectionFor(selected));
      if (response?.deferred) {
        say('Position en attente · termine le réglage en cours');
        timer = setTimeout(validate, 500);
        return;
      }
      acknowledged = value;
      if (version === generation) pending = false;
      rememberPending();
      say('Position validée · Enregistrer reste à ta main');
    } catch (error) { say(error.message); }
    finally { validating = false; toggle.disabled = false; applyNativeSelection(); updateSelectionControls(); }
  }
  async function enable() {
    if (connecting || validating) return;
    if (enabled) { enabled = false; toggle.setAttribute('aria-pressed','false'); document.documentElement.classList.remove('cdx-free-active'); selectElement(null); controls.hidden = true; say(pending ? 'Position en attente de validation.' : 'Déplacement désactivé.'); try { sessionStorage.removeItem('cdx-free-position-enabled'); } catch {} request('disconnect').catch(() => {}); return; }
    connecting = true; toggle.disabled = true; say('Connexion à Shopify…');
    try {
      // A local pending gesture is not a native Shopify draft. Only send the
      // last acknowledged value, then merge our pending differences separately.
      const response = await request('connect', acknowledged);
      selectionSupported = response.capabilities?.includes('select') === true;
      const base = parse(response.positions);
      if (pending) mergePending(base, parse(acknowledged), model);
      acknowledged = JSON.stringify(parse(response.positions)); model = base; applyAll();
      rememberPending();
      enabled = true; toggle.setAttribute('aria-pressed','true'); controls.hidden = false;
      document.documentElement.classList.add('cdx-free-active');
      try { sessionStorage.setItem('cdx-free-position-enabled','1'); } catch {}
      restoreView();
      if (response.selection && !pending) syncNativeSelection(response.selection);
      else applyNativeSelection();
      updateSelectionControls();
      if (pending) { if (!timer) schedule(); }
      else say('Clique et glisse un élément dans l’aperçu.');
    } catch (error) { say(error.message); }
    finally { connecting = false; toggle.disabled = false; updateSelectionControls(); }
  }
  toggle.addEventListener('click', enable);
  function mergePending(base, before, local) {
    for (const path of new Set([...Object.keys(before.compositions || {}), ...Object.keys(local.compositions || {})])) {
      if (JSON.stringify(before.compositions?.[path]) === JSON.stringify(local.compositions?.[path])) continue;
      base.compositions ||= {};
      if (local.compositions?.[path]) base.compositions[path] = {...local.compositions[path]};
      else delete base.compositions[path];
    }
    for (const id of new Set([...Object.keys(before.items), ...Object.keys(local.items)])) {
      const old = before.items[id], next = local.items[id];
      for (const device of ['wide', 'narrow']) {
        if (JSON.stringify(old?.[device]) === JSON.stringify(next?.[device])) continue;
        if (next?.[device]) base.items[id] = {...(base.items[id] || next), [device]: {...next[device]}};
        else if (base.items[id]) {
          delete base.items[id][device];
          if (!base.items[id].wide && !base.items[id].narrow) delete base.items[id];
        }
      }
    }
  }
  function label(el) {
    const data = meta(el); if (!data) return 'Élément';
    if (data.section) return 'Section complète';
    const type = data.id.split('__').pop().replace(/_[^_]+$/, '');
    const names = {text:'Texte',group:'Groupe parent',button:'Bouton',image:'Image',icon:'Icône',social_links:'Icônes sociales',card:'Carte','header-logo':'Logo','header-menu':'Menu','media-gallery':'Médias du produit','product-details':'Détails du produit','static-header':'En-tête','static-carousel-content':'Carrousel'};
    const name = names[type] || 'Bloc';
    const excerpt = ['text','button'].includes(type) ? el.textContent.replace(/\s+/g,' ').trim().slice(0,28) : '';
    return name + (excerpt ? ' · ' + excerpt : '');
  }
  function selectElement(el, preserveList = false) {
    resizeObserver?.disconnect();
    selected?.classList.remove('cdx-free-selected'); selected = el;
    selectedDescription = el && describe(el);
    if (!el) { select.replaceChildren(); ancestors = []; lockedSelection = false; updateSelectionControls(); return; }
    el.classList.add('cdx-free-selected');
    resizeObserver?.observe(el);
    if (!preserveList) {
      ancestors = []; for(let current = el; current; current = current.parentElement?.closest(SELECTOR)) { if (meta(current)) ancestors.push(current); }
      select.replaceChildren(...ancestors.map((node, index) => { const option = document.createElement('option'); option.value = String(index); option.textContent = label(node); return option; }));
    }
    const index = ancestors.indexOf(el); if (index >= 0) select.value = String(index);
    updateSelectionControls();
  }
  function updateSelectionControls() {
    if (composition.error) say('Composition inchangée : ' + composition.error);
    const valid = Boolean(selected?.isConnected && selected.getClientRects().length);
    select.disabled = validating || !valid;
    reset.disabled = validating || !valid || !model.items[selectedDescription?.id]?.[size()];
    updateResizeHandles();
  }
  let nativeSelection = null;
  function syncNativeSelection(selection) {
    if (drag) return;
    nativeSelection = selection;
    applyNativeSelection();
  }
  function applyNativeSelection() {
    if (!nativeSelection) return;
    if (!enabled || drag || validating) return;
    const id = nativeSelection?.path?.at(-1);
    const target = id && [...document.querySelectorAll(SELECTOR)].find(el => meta(el)?.id === id && el.getClientRects().length);
    lockedSelection = false;
    selectElement(target || null);
  }
  for (const eventName of ['shopify:block:select', 'shopify:section:select']) {
    document.addEventListener(eventName, event => {
      if (!enabled || drag || validating) return;
      const el = event.target?.closest?.(SELECTOR);
      const expectedId = nativeSelection?.path?.at(-1);
      const expected = expectedId && [...document.querySelectorAll(SELECTOR)].find(node => meta(node)?.id === expectedId && node.getClientRects().length);
      // A section load can follow the explicit selection of one of its blocks.
      // Keep that precise block; a deliberate parent selection arrives through
      // the bridge first and replaces nativeSelection with the parent's path.
      if (expected && (el === expected || el?.contains?.(expected))) { selectElement(expected); return; }
      nativeSelection = null;
      if (el && meta(el)) { lockedSelection = false; selectElement(el); }
    });
  }
  for (const eventName of ['shopify:block:deselect', 'shopify:section:deselect']) {
    document.addEventListener(eventName, event => {
      if (!drag && !validating && (event.target === selected || event.target?.contains?.(selected))) { nativeSelection = null; selectElement(null); }
    });
  }
  reconcileSelection = () => {
    applyNativeSelection();
    if (!selectedDescription || drag) { updateSelectionControls(); return; }
    const current = resolve(selectedDescription, selectedDescription.id);
    if (!current || !current.isConnected || !current.getClientRects().length) selectElement(null);
    else if (current !== selected || ancestors.some(el => !el.isConnected)) selectElement(current);
    else updateSelectionControls();
  };
  updateSelectionControls();
  select.addEventListener('change', () => { nativeSelection = null; selectElement(ancestors[Number(select.value)], true); lockedSelection = true; openSettings(selected); });
  function hit(event) {
    const candidates = [];
    for (const el of document.elementsFromPoint(event.clientX, event.clientY)) {
      if (panel.contains(el)) return null;
      const candidate = el.closest?.(SELECTOR);
      if (candidate && meta(candidate) && !candidates.includes(candidate)) candidates.push(candidate);
    }
    if (lockedSelection && selected && candidates.some(candidate => selected === candidate || selected.contains(candidate))) return selected;
    candidates.sort((a,b) => priority(b) - priority(a));
    return candidates[0] || null;
  }
  window.addEventListener('pointerdown', event => {
    if (!enabled || connecting || validating || event.button !== 0 || panel.contains(event.target)) return;
    const edge = event.target?.closest?.('#cdx-free-resize-ui [data-edge]')?.dataset.edge;
    const el = edge ? selected : hit(event); if (!el || (edge && !resizable(el))) return;
    const description = describe(el); if (!description || !description.selectors.length) { say('Cet élément ne peut pas encore être identifié durablement.'); return; }
    event.preventDefault(); event.stopImmediatePropagation(); stopTimer();
    request('gesture').catch(() => {});
    const preserve = lockedSelection && el === selected;
    nativeSelection = null;
    if (!preserve) lockedSelection = false;
    selectElement(el, preserve);
    const item = model.items[description.id]; const offset = item?.[size()] || {x:0,y:0};
    drag = {el, description, edge, height:resizeBox(el).getBoundingClientRect().height / composition.scale, scale:composition.scale, pointer:event.pointerId, startX:event.clientX, startY:event.clientY, scrollX, scrollY, base:{...offset}, oldItem:item ? JSON.parse(JSON.stringify(item)) : null, changed:false, device:size()};
    el.setPointerCapture?.(event.pointerId);
    document.documentElement.classList.add(edge ? 'cdx-free-resizing' : 'cdx-free-dragging');
    say(edge ? 'Règle la hauteur · Échap annule ce geste' : 'Déplace librement · Échap annule ce geste');
  }, true);
  window.addEventListener('pointermove', event => {
    if (!drag || event.pointerId !== drag.pointer) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const dx = (event.clientX - drag.startX + scrollX - drag.scrollX) / drag.scale, dy = (event.clientY - drag.startY + scrollY - drag.scrollY) / drag.scale;
    if (!drag.changed && (drag.edge ? Math.abs(dy) : Math.hypot(dx,dy)) * drag.scale < 3) return;
    drag.changed = true;
    let value;
    if (drag.edge) {
      const height = Math.round(Math.max(MIN_HEIGHT, Math.min(1000000, drag.height + (drag.edge === 'top' ? -dy : dy)))*10)/10;
      value = {...drag.base, height, resizeTop:(drag.base.resizeTop || 0) + (drag.edge === 'top' ? drag.height - height : 0)};
    } else value = {...drag.base, x:Math.round((drag.base.x + dx)*10)/10, y:Math.round((drag.base.y + dy)*10)/10};
    const item = model.items[drag.description.id] = {...(model.items[drag.description.id] || drag.description), [drag.device]:value};
    delete item.z; applyOne(drag.el, item[drag.device]); ensureForeground();
    updateResizeHandles();
    say(drag.edge ? 'Hauteur ' + value.height + ' px' : 'X ' + value.x + ' px · Y ' + value.y + ' px');
    if (event.clientY > innerHeight - 28) scrollBy(0,12); else if (event.clientY < 28) scrollBy(0,-12);
  }, true);
  function finish(cancel = false) {
    if (!drag) return;
    const current = drag;
    if (cancel) { if (current.oldItem) model.items[current.description.id] = current.oldItem; else delete model.items[current.description.id]; applyAll(); }
    else if (current.changed) { generation++; pending = true; rememberPending(); }
    try { current.el.releasePointerCapture(current.pointer); } catch {}
    drag = null; lockedSelection = false; document.documentElement.classList.remove('cdx-free-dragging','cdx-free-resizing'); updateSelectionControls();
    if (pending) schedule(); else say(cancel ? 'Geste annulé.' : 'Élément sélectionné.');
    if (!cancel && !current.changed && !current.edge) openSettings(current.el);
  }
  window.addEventListener('pointerup', event => { if (!drag || event.pointerId !== drag.pointer) return; event.preventDefault(); event.stopImmediatePropagation(); finish(); }, true);
  window.addEventListener('pointercancel', event => { if (drag && event.pointerId === drag.pointer) finish(true); }, true);
  window.addEventListener('keydown', event => { if (event.key === 'Escape' && drag) { event.preventDefault(); event.stopImmediatePropagation(); finish(true); } }, true);
  window.addEventListener('blur', () => { if (drag) finish(true); });
  window.addEventListener('click', event => { if (enabled && !panel.contains(event.target) && (resizeUI.contains(event.target) || hit(event))) { event.preventDefault(); event.stopImmediatePropagation(); } }, true);
  window.addEventListener('dragstart', event => { if (enabled && !panel.contains(event.target)) { event.preventDefault(); event.stopImmediatePropagation(); } }, true);
  reset.addEventListener('click', () => {
    if (!selected?.isConnected || !selected.getClientRects().length || validating) return;
    const data = describe(selected), item = model.items[data?.id]; if (!item?.[size()]) return;
    delete item[size()]; if (!item.wide && !item.narrow) delete model.items[data.id];
    request('gesture').catch(() => {});
    generation++; pending = true; rememberPending(); applyAll(); schedule();
  });
  window.addEventListener('beforeunload', event => { if (!validating && (pending || drag?.changed)) { event.preventDefault(); event.returnValue = ''; } });
  try { if (sessionStorage.getItem('cdx-free-position-enabled') === '1') enable(); else if (pending) schedule(); } catch {}
})();
